import { T } from '@tolgee/react';
import Link from 'next/link';
import { useSession } from 'next-auth/react';
import { CSSProperties, ReactNode } from 'react';
import styled from 'styled-components';

import theme from '../../../assets/theme';
import useAccessBadge, { LicenseCoarseLevels } from '../../../hooks/useAccessBadge';
import {
  featureGroups,
  FeatureRow,
  PlanCell,
  PlanId,
  planIdsFor,
  staticPlanColumns,
} from './planData';
import { BOOK_DEMO_HREF, QUOTE_REQUEST_HREF } from './PlanOverviewSection';
import {
  BORDER_WARM,
  CONTENT_MAX_WIDTH,
  HAIRLINE_WARM,
  HIGHLIGHT_BG,
  HIGHLIGHT_BORDER,
  HIGHLIGHT_TEXT,
  MOBILE_QUERY,
  MUTED_ITALIC,
  MUTED_LABEL,
  NARROW_QUERY,
  NAV_BAR_HEIGHT,
  NAVY,
  OUTLINE_BORDER,
  OUTLINE_HOVER_BG,
  SERIF_FONT,
  TABLET_QUERY,
  TEXT_STRONG,
} from './pricingTokens';

type CompareFeaturesSectionProps = {
  showIndividualPremium: boolean;
};

/** Only School & District gets the highlighted "recommended" column treatment. */
const isHighlightedPlan = (id: PlanId) => id === 'schoolDistrict';

type StickyCta = { label: ReactNode; href: string };

const stickyCtaConfig: Partial<Record<PlanId, StickyCta>> = {
  free: {
    label: <T keyName="Pricing.Compare.Footer.Free" defaultValue="Get started" />,
    href: '/signup',
  },
  schoolDistrict: {
    label: <T keyName="Pricing.Compare.Footer.School" defaultValue="Request a quote" />,
    href: QUOTE_REQUEST_HREF,
  },
  curriculumAlignment: {
    label: <T keyName="Pricing.Compare.Footer.Curriculum" defaultValue="Book a demo" />,
    href: BOOK_DEMO_HREF,
  },
  // Premium's real CTA needs the shared checkout/auth-guard state that lives in
  // PlanOverviewSection; duplicating it here risks two out-of-sync instances
  // of that state. Send this one back up to the interactive card instead.
  premium: {
    label: <T keyName="Pricing.Compare.Footer.Premium" defaultValue="See Premium" />,
    href: '#plans',
  },
};

type StickyCtaFlags = {
  isAuthenticated: boolean;
  isFreemium: boolean;
  isEnterprise: boolean;
  isOnPaidIndividualPlan: boolean;
};

// A function component rather than a pre-built element: `HeaderCtaNote` is a
// styled-component declared later in this file, and referencing it while
// building a plain JSX element up here (eagerly, at module-evaluation time)
// would hit the temporal dead zone. Calling this at render time instead
// means `HeaderCtaNote` is already initialised by then.
const CurrentPlanLabel = () => (
  <HeaderCtaNote>
    <T keyName="Your current plan" defaultValue="Your current plan" />
  </HeaderCtaNote>
);

/**
 * Mirrors the CTA-visibility rules in `PlanOverviewSection` — a signed-in
 * freemium/enterprise/Premium visitor shouldn't be pushed back into signup,
 * a quote request, or checkout for a plan they're already on.
 */
const stickyCtaFor = (
  id: PlanId,
  flags: StickyCtaFlags,
): { cta?: StickyCta; replacement?: ReactNode } => {
  switch (id) {
    case 'free':
      if (flags.isAuthenticated) {
        return flags.isFreemium ? { replacement: <CurrentPlanLabel /> } : {};
      }
      return { cta: stickyCtaConfig.free };
    case 'schoolDistrict':
      return flags.isEnterprise
        ? { replacement: <CurrentPlanLabel /> }
        : { cta: stickyCtaConfig.schoolDistrict };
    case 'premium':
      return flags.isOnPaidIndividualPlan
        ? { replacement: <CurrentPlanLabel /> }
        : { cta: stickyCtaConfig.premium };
    case 'curriculumAlignment':
    default:
      return { cta: stickyCtaConfig.curriculumAlignment };
  }
};

export const CompareFeaturesSection = ({
  showIndividualPremium,
}: CompareFeaturesSectionProps) => {
  const planIds = planIdsFor(showIndividualPremium);

  const session = useSession();
  const isAuthenticated = session.status === 'authenticated';
  const { accessBadge: userAccessBadge } = useAccessBadge();
  const isFreemium =
    userAccessBadge?.license?.coarseLevel === LicenseCoarseLevels.freemium;
  // Not just "authenticated and not freemium" — that also matched Enterprise
  // licence holders, marking School & District customers as "already on
  // Premium" too.
  const isOnPaidIndividualPlan =
    userAccessBadge?.license?.coarseLevel === LicenseCoarseLevels.premium;
  const isEnterprise =
    userAccessBadge?.license?.coarseLevel === LicenseCoarseLevels.enterprise;

  return (
    <Section id="compare">
      <Inner>
        <Intro>
          <SectionHeading>
            <T
              keyName="Pricing.Compare.Heading"
              defaultValue="Compare every writing feature"
            />
          </SectionHeading>
        </Intro>

        <Card>
          <Scroller>
            <Table style={{ '--plan-count': planIds.length } as CSSProperties}>
              <StickyHeaderGroup>
                <TopHeaderRow>
                  <TopHeaderFeatureCell>
                    <TopHeaderEyebrow>
                      <T keyName="Pricing.Compare.FeatureColumn" defaultValue="Feature" />
                    </TopHeaderEyebrow>
                  </TopHeaderFeatureCell>
                  {planIds.map(id => (
                    <TopHeaderPlanCell key={id} $highlighted={isHighlightedPlan(id)}>
                      <TopHeaderPlanName $highlighted={isHighlightedPlan(id)}>
                        {staticPlanColumns[id].shortLabel}
                      </TopHeaderPlanName>
                      <TopHeaderPlanPrice
                        $highlighted={isHighlightedPlan(id)}
                        data-testid={`header-price-${id}`}
                      >
                        {staticPlanColumns[id].price ?? (
                          <T
                            keyName="Pricing.Compare.CustomPricing"
                            defaultValue="Custom pricing"
                          />
                        )}
                      </TopHeaderPlanPrice>
                    </TopHeaderPlanCell>
                  ))}
                </TopHeaderRow>

                {/* Kept visible alongside the sticky header so a visitor can act
                    without scrolling all the way back down to the footer row. */}
                <HeaderCtaRow>
                  <HeaderCtaFeatureCell />
                  {planIds.map(id => {
                    const { cta, replacement } = stickyCtaFor(id, {
                      isAuthenticated,
                      isFreemium,
                      isEnterprise,
                      isOnPaidIndividualPlan,
                    });
                    return (
                      <HeaderCtaCell
                        key={id}
                        $highlighted={isHighlightedPlan(id)}
                        data-testid={`header-cta-${id}`}
                      >
                        {replacement ??
                          (cta && (
                            <Link href={cta.href} passHref>
                              <HeaderCtaButton $highlighted={isHighlightedPlan(id)}>
                                {cta.label}
                              </HeaderCtaButton>
                            </Link>
                          ))}
                      </HeaderCtaCell>
                    );
                  })}
                </HeaderCtaRow>
              </StickyHeaderGroup>

              {featureGroups.map((group, groupIndex) => (
                <GroupBlock key={group.id}>
                  <GroupDividerRow>
                    <GroupDividerLabel>{group.heading}</GroupDividerLabel>
                    {planIds.map(id => (
                      <GroupDividerCell key={id} $highlighted={isHighlightedPlan(id)} />
                    ))}
                  </GroupDividerRow>

                  {group.rows.map((row, rowIndex) => (
                    <Row
                      key={row.id}
                      $isLast={rowIndex === group.rows.length - 1}
                      // The last row of the last group is now the table's last
                      // visual element (the footer row was removed) — it needs
                      // to clip its own corners to match Card's rounded bottom,
                      // the same way TopHeaderRow clips the top.
                      $isTableEnd={
                        groupIndex === featureGroups.length - 1 &&
                        rowIndex === group.rows.length - 1
                      }
                    >
                      <FeatureNameCell>
                        <FeatureName>{row.name}</FeatureName>
                        {row.description && (
                          <FeatureDescription>{row.description}</FeatureDescription>
                        )}
                      </FeatureNameCell>
                      {planIds.map(id => (
                        <ValueCell key={id} $highlighted={isHighlightedPlan(id)}>
                          <StackedPlanLabel>
                            {staticPlanColumns[id].shortLabel}
                          </StackedPlanLabel>
                          <ValueContent data-testid={`feature-value-${row.id}-${id}`}>
                            {renderCell(row, id)}
                          </ValueContent>
                        </ValueCell>
                      ))}
                    </Row>
                  ))}
                </GroupBlock>
              ))}
            </Table>
          </Scroller>
        </Card>
      </Inner>
    </Section>
  );
};

const renderCell = (row: FeatureRow, planId: PlanId) => {
  const cell: PlanCell = row.cells[planId];
  switch (cell.kind) {
    case 'check':
      return (
        <Check aria-label="Included">
          <span aria-hidden="true">✓</span>
        </Check>
      );
    case 'none':
      return (
        <Dash aria-label="Not included">
          <span aria-hidden="true">—</span>
        </Dash>
      );
    default:
      return <ValueText $emphasis={!!cell.emphasis}>{cell.node}</ValueText>;
  }
};

/* ─── Layout ──────────────────────────────────────────────────────────────── */

const Section = styled.section`
  width: 100%;
  background: #fdf3ed;
  padding: 84px 32px;

  @media ${MOBILE_QUERY} {
    padding: 56px 20px;
  }
`;

const Inner = styled.div`
  max-width: ${CONTENT_MAX_WIDTH};
  margin: 0 auto;
`;

const Intro = styled.div`
  margin-bottom: 28px;
`;

const SectionHeading = styled.h2`
  font-size: clamp(28px, 3.4vw, 38px);
  font-weight: 900;
  letter-spacing: -0.015em;
  margin: 0;
  color: ${NAVY};
`;

// No unconditional overflow: hidden here — StickyHeaderGroup's sticky
// positioning resolves against the nearest ancestor scroll container, and
// Card would become that container (while never itself scrolling),
// permanently stranding the sticky header instead of letting it stick to the
// viewport. The rounded corners it used to clip are handled locally by
// TopHeaderRow and the last feature row instead; on mobile the sticky group
// doesn't render at all, so Card clips there like before.
const Card = styled.div`
  background: ${theme.colors.white};
  border: 1px solid ${BORDER_WARM};
  border-radius: 16px;

  @media ${MOBILE_QUERY} {
    overflow: hidden;
  }
`;

/**
 * Five columns stop fitting before the mobile card layout kicks in, so between
 * 993px and 1200px the table scrolls sideways inside the card with the feature
 * name pinned. Below 992px there is nothing to scroll — the rows are cards.
 */
const Scroller = styled.div`
  @media ${TABLET_QUERY} {
    overflow-x: auto;
  }

  @media ${MOBILE_QUERY} {
    overflow-x: visible;
  }
`;

const Table = styled.div`
  @media ${TABLET_QUERY} {
    min-width: calc(280px + var(--plan-count) * 175px);
  }

  @media ${MOBILE_QUERY} {
    min-width: 0;
  }
`;

const gridColumns = `
  display: grid;
  grid-template-columns: 1.7fr repeat(var(--plan-count), 1fr);
`;

/* ─── Top sticky header (feature / plan name + price, once for the table) ─── */

/**
 * Groups the header row and its CTA row under one sticky element, so both
 * stick together as a unit — a visitor scrolling through the long feature
 * list keeps the plan names *and* a way to act on them in view. `Card` must
 * stay `overflow: visible` at this width or this stops sticking entirely
 * (see the comment on `Card` above).
 *
 * `top: NAV_BAR_HEIGHT`, not `0` — `LandingPageHeader`'s own nav bar is
 * `position: fixed; top: 0` with a z-index far above this page, so sticking
 * at `top: 0` here lands this group right underneath it instead of below it.
 */
const StickyHeaderGroup = styled.div`
  position: sticky;
  top: ${NAV_BAR_HEIGHT};
  z-index: 5;

  @media ${MOBILE_QUERY} {
    display: none;
  }
`;

const TopHeaderRow = styled.div`
  ${gridColumns}
  background: ${theme.colors.white};
  border-bottom: 1px solid #e1d6c8;
  overflow: hidden;
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
`;

const TopHeaderFeatureCell = styled.div`
  padding: 22px 28px;
  display: flex;
  align-items: flex-end;
`;

const TopHeaderEyebrow = styled.span`
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: ${MUTED_LABEL};
`;

const TopHeaderPlanCell = styled.div<{ $highlighted: boolean }>`
  padding: 22px 20px;
  text-align: center;
  ${({ $highlighted }) =>
    $highlighted &&
    `background: ${HIGHLIGHT_BG}; border-left: 1px solid ${HIGHLIGHT_BORDER}; border-right: 1px solid ${HIGHLIGHT_BORDER}; box-shadow: inset 0 3px 0 ${theme.colors.orange};`}
`;

const TopHeaderPlanName = styled.div<{ $highlighted: boolean }>`
  font-size: 17px;
  font-weight: 900;
  color: ${NAVY};
`;

const TopHeaderPlanPrice = styled.div<{ $highlighted: boolean }>`
  font-family: ${SERIF_FONT};
  font-size: 15px;
  color: ${({ $highlighted }) => ($highlighted ? HIGHLIGHT_TEXT : MUTED_LABEL)};
  margin-top: 2px;
`;

/* ─── Sticky header's CTA row ──────────────────────────────────────────────── */

const HeaderCtaRow = styled.div`
  ${gridColumns}
  background: ${theme.colors.white};
  border-bottom: 1px solid #e1d6c8;
`;

const HeaderCtaFeatureCell = styled.div``;

const HeaderCtaCell = styled.div<{ $highlighted: boolean }>`
  padding: 14px 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  ${({ $highlighted }) =>
    $highlighted &&
    `background: ${HIGHLIGHT_BG}; border-left: 1px solid ${HIGHLIGHT_BORDER}; border-right: 1px solid ${HIGHLIGHT_BORDER};`}
`;

const HeaderCtaButton = styled.a<{ $highlighted: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 38px;
  width: 100%;
  padding: 0 18px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s ease;

  ${({ $highlighted }) =>
    $highlighted
      ? `
    background: ${theme.colors.orange};
    color: ${theme.colors.white};
    &:hover { background: ${theme.colors.orangeDark}; }
  `
      : `
    border: 1.5px solid ${OUTLINE_BORDER};
    color: ${NAVY};
    &:hover { border-color: ${theme.colors.orange}; color: ${theme.colors.orangeDark}; background: ${OUTLINE_HOVER_BG}; }
  `}
`;

const HeaderCtaNote = styled.span`
  font-size: 13px;
  font-weight: 800;
  color: ${NAVY};
  text-align: center;
`;

/* ─── Group divider ("Content", "Classroom controls & reporting", ...) ────── */

const GroupBlock = styled.div``;

const GroupDividerRow = styled.div`
  ${gridColumns}
  background: #fbf7f2;
  border-top: 1px solid ${BORDER_WARM};
  border-bottom: 1px solid ${BORDER_WARM};

  @media ${MOBILE_QUERY} {
    display: block;
    padding: 16px 20px;
  }
`;

const GroupDividerLabel = styled.div`
  padding: 14px 28px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: ${MUTED_ITALIC};

  @media ${MOBILE_QUERY} {
    padding: 0;
  }
`;

const GroupDividerCell = styled.div<{ $highlighted: boolean }>`
  ${({ $highlighted }) =>
    $highlighted &&
    `background: ${HIGHLIGHT_BG}; border-left: 1px solid ${HIGHLIGHT_BORDER}; border-right: 1px solid ${HIGHLIGHT_BORDER};`}

  @media ${MOBILE_QUERY} {
    display: none;
  }
`;

/* ─── Feature rows ─────────────────────────────────────────────────────────── */

const Row = styled.div<{ $isLast: boolean; $isTableEnd: boolean }>`
  ${gridColumns}
  border-top: 1px solid ${HAIRLINE_WARM};
  ${({ $isTableEnd }) =>
    $isTableEnd &&
    `overflow: hidden; border-bottom-left-radius: 16px; border-bottom-right-radius: 16px;`}

  @media ${MOBILE_QUERY} {
    display: block;
    padding: 18px 20px;
    border-top: none;
    border-bottom: 1px solid ${HAIRLINE_WARM};
  }
`;

const FeatureNameCell = styled.div`
  padding: 18px 28px;

  @media ${TABLET_QUERY} {
    position: sticky;
    left: 0;
    background: ${theme.colors.white};
    z-index: 1;
  }

  @media ${MOBILE_QUERY} {
    position: static;
    padding: 0 0 4px;
    margin-bottom: 4px;
  }
`;

const FeatureName = styled.div`
  font-size: 15.5px;
  font-weight: 700;
  line-height: 1.45;
  color: ${NAVY};

  @media ${NARROW_QUERY} {
    font-size: 15px;
  }
`;

const FeatureDescription = styled.div`
  font-family: ${SERIF_FONT};
  font-size: 15px;
  line-height: 1.5;
  color: ${MUTED_ITALIC};
  margin-top: 5px;
  max-width: 460px;
`;

const ValueCell = styled.div<{ $highlighted: boolean }>`
  padding: 18px 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  ${({ $highlighted }) =>
    $highlighted &&
    `background: ${HIGHLIGHT_BG}; border-left: 1px solid ${HIGHLIGHT_BORDER}; border-right: 1px solid ${HIGHLIGHT_BORDER};`}

  @media ${MOBILE_QUERY} {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: baseline;
    gap: 12px;
    text-align: left;
    padding: 10px 0 0;
    background: none;
    border: none;
  }

  /* On a phone a long value wraps back under the label and the two read as one
     sentence ("FREE Weekly sessions included…"). Stack them instead. */
  @media ${NARROW_QUERY} {
    grid-template-columns: 1fr;
    gap: 2px;
  }
`;

/** Only visible once the table has collapsed into cards. */
const StackedPlanLabel = styled.span`
  display: none;

  @media ${MOBILE_QUERY} {
    display: block;
    font-size: 12px;
    font-weight: 900;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: ${MUTED_ITALIC};
  }
`;

const ValueContent = styled.span`
  display: block;

  @media ${MOBILE_QUERY} {
    text-align: right;
    /* "All test prep libraries" has no break opportunity that fits a 320px column. */
    overflow-wrap: anywhere;
  }

  @media ${NARROW_QUERY} {
    text-align: left;
  }
`;

const Check = styled.span`
  font-size: 18px;
  font-weight: 900;
  color: ${theme.colors.orange};
`;

const Dash = styled.span`
  font-size: 18px;
  color: #c9c1b6;
`;

const ValueText = styled.span<{ $emphasis: boolean }>`
  font-size: ${({ $emphasis }) => ($emphasis ? '15px' : '14.5px')};
  font-weight: ${({ $emphasis }) => ($emphasis ? 700 : 600)};
  line-height: 1.45;
  color: ${({ $emphasis }) => ($emphasis ? TEXT_STRONG : '#4c505f')};

  a {
    font-weight: 800;
  }

  @media ${NARROW_QUERY} {
    font-size: ${({ $emphasis }) => ($emphasis ? '14px' : '13px')};
  }
`;

export default CompareFeaturesSection;
